import { all, fork, getContext, put, race, select, take } from 'redux-saga/effects';
import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { buffers, eventChannel } from 'redux-saga';
import { PapiSession } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import { getHubState } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { receiveHubMessage } from '@wix/da-shared-react/pkg/redux/hub/actions';
import { appVisibilityChange } from '@wix/da-shared-react/pkg/redux/app/actions';

function* listenToHubChannels() {
  const client: SupabaseClient = yield getContext('supaClient');

  const hub: PapiSession['hub'] = yield select(getHubState);

  // Single hub channel (e.g. hub:usertokenhash); getHubState still returns array with one item
  const channelName = hub?.subs?.[0];
  if (!channelName) {
    return;
  }

  // Don't subscribe if page is hidden - wait for it to become visible
  if (typeof document !== 'undefined' && document.hidden) {
    ddt.log('hub', 'Realtime hub channels not started, page is hidden. Waiting for visibility...');
    // Wait for visibility change to visible
    const visibilityAction = yield take(appVisibilityChange);
    if (visibilityAction.payload.hidden) {
      // Still hidden, return (shouldn't happen but handle gracefully)
      ddt.log('hub', 'Document still hidden after visibility change event');
      return;
    }
    ddt.log('hub', 'Document became visible, starting hub channels');
  }

  try {
    let hubChannel: RealtimeChannel | null = null;

    const sagaChannel = eventChannel(emitter => {
      try {
        hubChannel = client.realtime.channel(channelName, {
          config: {
            broadcast: {
              ack: false,
              self: false,
            },
          },
        });

        // Backend sends { messages: [{ chan, data }, ...] }
        hubChannel.on('broadcast', { event: 'broadcast' }, payload => {
          ddt.log('hub', 'Realtime event received', payload);
          const messages = payload?.payload?.messages;
          if (Array.isArray(messages)) {
            for (const msg of messages) {
              if (msg && typeof msg.chan !== 'undefined' && msg.data !== undefined) {
                emitter({ channel: msg.chan, data: msg.data });
              }
            }
          }
        });

        hubChannel.subscribe((status, err) => {
          ddt.log('hub', `Hub channel ${channelName} subscription status changed`, { status, err });

          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            if (!document.hidden) {
              ddt.log('hub', 'Hub channel error detected, triggering recovery', {
                channelName,
                status,
                err,
              });
              hubChannel?.rejoinTimer?.scheduleTimeout();
            }
          }

          if (status === 'SUBSCRIBED') {
            ddt.log('hub', `Successfully subscribed to hub channel ${channelName}`);
          }

          if (status === 'CLOSED') {
            ddt.log('hub', `Hub channel ${channelName} closed`);
          }

          if (err && err.message !== '') {
            ddt.error(
              'hub',
              `Realtime subscription error for hub channel ${channelName} - ${status}`,
              err
            );
          }
        });
      } catch (err) {
        ddt.error('hub', `Error creating hub channel ${channelName}`, err);
      }

      return () => {
        if (hubChannel) {
          try {
            void hubChannel.unsubscribe();
            void client.removeChannel(hubChannel);
          } catch (err) {
            ddt.error('hub', 'Error cleaning up hub channel', err);
          }
          hubChannel = null;
        }
      };
    }, buffers.expanding<any>());

    while (true) {
      try {
        const payload = yield race({
          chanRes: take(sagaChannel),
        });

        if (payload.chanRes) {
          ddt.log('hub', 'Realtime event received', payload.chanRes);

          const { channel, data } = payload.chanRes;
          if (channel != null && data !== undefined) {
            yield put(receiveHubMessage(channel, { payload: data }));
          }
        }
      } catch (err) {
        ddt.error('hub', 'Realtime processing error', err);
        sagaChannel.close();
      }
    }
  } catch (err) {
    ddt.error('hub', 'Error creating saga channel', err);
  }
}

/**
 * Listens to realtime events for hub notifications
 */
export function* hubSaga() {
  yield all([fork(listenToHubChannels)]);
}
